import React, { useRef, useState } from 'react';
import { ApiError, apiFetch } from '../../lib/api';
import type { TournamentDetail } from '../../hooks/useScoringTournament';
import { PinDialog } from '../common/PinDialog';

interface RemoveShooterDialogProps {
  tournamentId: string;
  tournamentTitle: string;
  shooterId: string;
  shooterName: string;
  shooterBib: string | null;
  /** Saved shots that will go with the shooter. */
  shotCount: number;
  onClose: () => void;
  onRemoved: (result: { removedShots: number; leaderboard: TournamentDetail['leaderboard'] }) => void;
}

/** Confirms removing a shooter from one tournament. The PIN is checked on the server only. */
export function RemoveShooterDialog({
  tournamentId,
  tournamentTitle,
  shooterId,
  shooterName,
  shooterBib,
  shotCount,
  onClose,
  onRemoved,
}: RemoveShooterDialogProps) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busyRef = useRef(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!pin.trim() || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const body = await apiFetch<{ removedShots: number; leaderboard: TournamentDetail['leaderboard'] }>(
        `/api/admin/shooting/tournaments/${tournamentId}/shooters/${shooterId}`,
        { method: 'DELETE', body: JSON.stringify({ pin: pin.trim() }) },
      );
      onRemoved({ removedShots: body.removedShots, leaderboard: body.leaderboard });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove the shooter. Try again.');
      setPin('');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  const who = shooterBib ? `${shooterName} (${shooterBib})` : shooterName;
  const shots =
    shotCount === 0 ? 'They have no saved shots.' : `Their ${shotCount} saved shot${shotCount === 1 ? '' : 's'} will also be removed.`;
  return (
    <PinDialog
      titleId="remove-shooter-title"
      title={`Remove ${who}?`}
      description={`This takes ${shooterName} off ${tournamentTitle} and the public board. ${shots} Their event registration and payment are not touched.`}
      pin={pin}
      onPinChange={(next) => {
        setPin(next);
        setError(null);
      }}
      error={error}
      busy={busy}
      confirmLabel="Remove shooter"
      busyLabel="Removing…"
      onSubmit={(event) => void submit(event)}
      onClose={onClose}
    />
  );
}
